import {
  MenuItem,
  Select,
  SelectChangeEvent,
  FormControl,
  InputLabel,
  AutocompleteInputChangeReason,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import useSearch from "../../../../../hooks/useSearch";
import { switchAgentSession } from "../../../../../store/actions/sessionActions";
import { hideModal } from "../../../../../store/slices/modalSlice";
import { useTypedDispatch } from "../../../../../store/store";
import {
  ChangeValue,
  SearchAutocomplete,
} from "../../../../shared/SearchAutocomplete";

import styles from "./index.module.scss";
import { SwitchModalSubmit } from "./styled";

type SearchItem = Record<string, string>;

type Agency = Record<string, string> | null;

function SwitchAgentModal() {
  const dispatch = useTypedDispatch();
  const { t } = useTranslation();

  const { agencies, agents, searchHandlers, resetHandlers } = useSearch();

  const [agencyValue, setAgencyValue] = useState<Agency>(null);
  const [agentValue, setAgentValue] = useState("");
  const [agencyInput, setAgencyInput] = useState("");

  const [chosenAgent, setChosenAgent] = useState<SearchItem | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const agentsData = useMemo(
    () => agents.results.map((el) => ({ value: el.id, label: el.name })),
    [agents.results],
  );

  useEffect(() => {
    setChosenAgent(null);
    setAgentValue("");
    resetHandlers.agentsReset();
  }, [agencyValue?.id]);

  const searchAgency = async (value: string) => {
    if (value) {
      await searchHandlers.agencySearch(value);
    } else {
      resetHandlers.agenciesReset();
    }
  };

  const searchAgent = async (value: string) => {
    if (value) {
      await searchHandlers.agentSearch(value);
    } else {
      resetHandlers.agentsReset();
    }
  };

  const onInputChange = (
    _: React.SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (reason !== "input") {
      return;
    }

    setAgencyInput(String(value));

    searchAgency(value);
  };

  const handleAgentInputChange = (e: SelectChangeEvent) => {
    const value = e.target.value;

    const selectedAgent = agentsData.find((agent) => agent.value === value);
    setAgentValue(value);
    if (!selectedAgent) {
      setChosenAgent(null);

      return;
    }

    setChosenAgent({
      id: selectedAgent.value,
      name: selectedAgent.label,
    });
  };

  const handleAgencyChosenChange = async (
    _: React.SyntheticEvent,
    value: ChangeValue | null,
  ) => {
    setAgencyValue(value);

    if (!value) {
      setAgencyInput("");

      return;
    }

    setAgencyInput(value.name);
    await searchAgent(String(value.id));
  };

  const isAgentDisabled = !agencyValue;
  const isSubmitDisabled = !agencyValue || !chosenAgent || isSubmitting;

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    if (!agencyValue || !chosenAgent || isSubmitting) {
      return;
    }

    try {
      setIsSubmitting(true);

      const result = await dispatch(
        switchAgentSession({
          agencyId: agencyValue.id,
          agentId: chosenAgent.id,
          agentName: chosenAgent.name,
        }),
      ).unwrap();

      if (result?.key) {
        resetHandlers.agenciesReset();
        resetHandlers.agentsReset();
        dispatch(hideModal());
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.inputs}>
          <SearchAutocomplete
            options={agencies.results}
            onChange={handleAgencyChosenChange}
            onInputChange={onInputChange}
            value={agencyValue}
            inputValue={agencyInput}
            label={t("Agency")}
          />
        </div>

        <FormControl fullWidth>
          <InputLabel
            id="agent-label"
            sx={{
              top: "6px",
              fontFamily: "Montserrat, sans-serif",
              color: "var(--input-placeholder-color, #8C8C8C)",

              "&.Mui-focused": {
                color: "var(--input-focus-border-color, #666666)",
                top: "3px",
              },
            }}>
            {t("Agent")}
          </InputLabel>

          <Select
            value={agentValue}
            labelId="agent-label"
            label={t("Agent")}
            disabled={isAgentDisabled || agents.isLoading}
            onChange={handleAgentInputChange}
            sx={{
              height: "66px",
              border: "2px solid #EBEBEB",
              borderRadius: "0",
              "& .Mui-focused": {
                border: "2px solid #EBEBEB",
              },
              fontFamily: "Montserrat, sans-serif",
            }}
            MenuProps={{
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "left",
              },
              PaperProps: {
                onMouseDown: (e: React.MouseEvent) => {
                  e.stopPropagation();
                },
                onClick: (e: React.MouseEvent) => {
                  e.stopPropagation();
                },
              },
            }}>
            {agentsData.map((item) => (
              <MenuItem
                key={item.value}
                value={item.value}
                sx={{ fontFamily: "Montserrat, sans-serif" }}>
                {item.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <div className={styles.inputs}>
          <SwitchModalSubmit type="submit" disabled={isSubmitDisabled}>
            {t("Switch session")}
          </SwitchModalSubmit>
        </div>
      </form>
    </div>
  );
}

export default SwitchAgentModal;
